import React, { useState, useEffect, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Gavel, Landmark, HeartPulse, FileSignature, Coins, ScrollText,
  Users, Folder, FileText, Upload, FolderOpen, ChevronRight, Plus,
  Edit, Save, X, Search, Check, Link2, ExternalLink
} from 'lucide-react';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const ICON_MAP = {
  'gavel': Gavel, 'landmark': Landmark, 'heart-pulse': HeartPulse,
  'file-signature': FileSignature, 'coins': Coins, 'scroll': ScrollText,
  'users': Users, 'folder': Folder,
};

function getIcon(iconName) {
  return ICON_MAP[iconName] || Folder;
}

// ===== Hauptkomponente =====
export function VorgaengeKacheln({ klientId, onRefresh }) {
  const [vorgaenge, setVorgaenge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openVorgang, setOpenVorgang] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const loadVorgaenge = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}`);
      if (res.ok) setVorgaenge(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [klientId]);

  useEffect(() => { loadVorgaenge(); }, [loadVorgaenge]);

  const aktive = vorgaenge.filter(v => v.status === 'aktiv');
  const beendete = vorgaenge.filter(v => v.status === 'beendet');

  // Gruppiere nach Kategorie
  const grouped = {};
  aktive.forEach(v => {
    const kat = v.kategorie_label || 'Sonstiges';
    if (!grouped[kat]) grouped[kat] = { farbe: v.farbe, icon: v.icon, items: [] };
    grouped[kat].items.push(v);
  });

  if (loading) return <div className="text-center py-4 text-slate-400 text-sm">Lade Vorgänge...</div>;

  return (
    <>
      <div className="space-y-4">
        {Object.entries(grouped).map(([katLabel, { farbe, icon, items }]) => {
          const KatIcon = getIcon(icon);
          return (
            <div key={katLabel}>
              <div className="flex items-center gap-2 mb-2">
                <KatIcon className="w-3.5 h-3.5" style={{ color: farbe }} />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{katLabel}</span>
                <span className="text-xs text-slate-300">({items.length})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {items.map(v => (
                  <button
                    key={v.id}
                    data-testid={`vorgang-kachel-${v.typ}`}
                    onClick={() => setOpenVorgang(v)}
                    className="group relative text-left p-3 rounded-xl border-2 transition-all duration-200 cursor-pointer bg-white hover:shadow-md"
                    style={{ borderColor: `${v.farbe}30` }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = v.farbe}
                    onMouseLeave={e => e.currentTarget.style.borderColor = `${v.farbe}30`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-slate-800 truncate">{v.bezeichnung || v.typ_label}</p>
                        {v.aktenzeichen && <p className="text-xs text-slate-400 mt-0.5 truncate">Az: {v.aktenzeichen}</p>}
                        {v.betrag > 0 && <p className="text-xs text-slate-500 mt-0.5">{Number(v.betrag).toLocaleString('de-DE')} EUR / {v.turnus || 'mtl.'}</p>}
                        {v.institution && <p className="text-xs text-slate-400 mt-0.5 truncate">{v.institution}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
                        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500" />
                        {v.dokumente_count > 0 && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{v.dokumente_count}</Badge>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })}

        {aktive.length === 0 && !loading && (
          <p className="text-center text-sm text-slate-400 py-2">Noch keine Vorgänge angelegt</p>
        )}

        {beendete.length > 0 && (
          <details className="text-sm">
            <summary className="text-slate-400 cursor-pointer hover:text-slate-600 text-xs">
              {beendete.length} beendete(r) Vorgang/Vorgänge
            </summary>
            <div className="mt-2 space-y-1">
              {beendete.map(v => (
                <div key={v.id} className="flex items-center gap-2 px-3 py-1.5 rounded bg-slate-50 text-slate-500 text-xs">
                  <span>{v.bezeichnung || v.typ_label}</span>
                  {v.ende_datum && <span>bis {new Date(v.ende_datum).toLocaleDateString('de-DE')}</span>}
                </div>
              ))}
            </div>
          </details>
        )}

        <Button
          variant="outline" size="sm" className="w-full border-dashed"
          onClick={() => setShowAddDialog(true)}
          data-testid="vorgang-erstellen-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Vorgang erstellen
        </Button>
      </div>

      {openVorgang && (
        <VorgangSheet
          vorgang={openVorgang} klientId={klientId}
          onClose={() => setOpenVorgang(null)}
          onUpdate={() => { loadVorgaenge(); onRefresh?.(); }}
        />
      )}
      {showAddDialog && (
        <VorgangErstellen
          klientId={klientId}
          onClose={() => setShowAddDialog(false)}
          onCreated={() => { setShowAddDialog(false); loadVorgaenge(); onRefresh?.(); }}
        />
      )}
    </>
  );
}

// ===== Vorgang Detail-Sheet =====
function VorgangSheet({ vorgang, klientId, onClose, onUpdate }) {
  const [dokumente, setDokumente] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [allDocs, setAllDocs] = useState([]);
  const Icon = getIcon(vorgang.icon);

  const loadDokumente = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgang.id}/dokumente`);
      if (res.ok) setDokumente(await res.json());
    } catch (e) { console.error(e); }
    setLoadingDocs(false);
  }, [klientId, vorgang.id]);

  useEffect(() => { loadDokumente(); }, [loadDokumente]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgang.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      if (res.ok) { setEditMode(false); onUpdate(); }
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('datei', file);
      formData.append('klient_id', klientId);
      formData.append('dokument_kategorie', vorgang.typ);
      const res = await fetch(`${API}/api/betreuung/dokumente/upload`, { method: 'POST', body: formData });
      if (res.ok) {
        const newDoc = await res.json();
        // Direkt zuordnen
        await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgang.id}/dokumente/zuordnen`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dokument_id: newDoc.id }),
        });
        loadDokumente();
      }
    } catch (e) { console.error(e); }
    setUploading(false);
    e.target.value = '';
  };

  const handleAssign = async (docId) => {
    try {
      await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgang.id}/dokumente/zuordnen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dokument_id: docId }),
      });
      loadDokumente();
      setShowAssign(false);
    } catch (e) { console.error(e); }
  };

  const loadAllDocs = async () => {
    try {
      const res = await fetch(`${API}/api/betreuung/dokumente?klient_id=${klientId}`);
      if (res.ok) {
        const data = await res.json();
        setAllDocs(Array.isArray(data) ? data : data.dokumente || []);
      }
    } catch (e) { console.error(e); }
    setShowAssign(true);
  };

  const handleStatusChange = async (s) => {
    try {
      await fetch(`${API}/api/vorgaenge/klient/${klientId}/${vorgang.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: s }),
      });
      onUpdate(); onClose();
    } catch (e) { console.error(e); }
  };

  return (
    <Sheet open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid={`vorgang-sheet-${vorgang.typ}`}>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" style={{ color: vorgang.farbe }} />
            {vorgang.bezeichnung || vorgang.typ_label}
          </SheetTitle>
          <p className="text-xs text-slate-400">{vorgang.kategorie_label}</p>
        </SheetHeader>

        <Tabs defaultValue="details" className="mt-4">
          <TabsList className="w-full">
            <TabsTrigger value="details" className="flex-1" data-testid="vorgang-tab-details">Details</TabsTrigger>
            <TabsTrigger value="dokumente" className="flex-1" data-testid="vorgang-tab-dokumente">
              Dokumente ({dokumente.length})
            </TabsTrigger>
          </TabsList>

          {/* Details */}
          <TabsContent value="details" className="space-y-4 mt-4">
            {editMode ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500">Bezeichnung</label>
                  <Input defaultValue={vorgang.bezeichnung || ''} onChange={e => setEditData(p => ({ ...p, bezeichnung: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Aktenzeichen</label>
                  <Input defaultValue={vorgang.aktenzeichen || ''} onChange={e => setEditData(p => ({ ...p, aktenzeichen: e.target.value }))} data-testid="vorgang-input-aktenzeichen" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Institution / Behörde</label>
                  <Input defaultValue={vorgang.institution || ''} onChange={e => setEditData(p => ({ ...p, institution: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Sachbearbeiter</label>
                  <Input defaultValue={vorgang.sachbearbeiter || ''} onChange={e => setEditData(p => ({ ...p, sachbearbeiter: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-500">Betrag (EUR)</label>
                    <Input type="number" defaultValue={vorgang.betrag || ''} onChange={e => setEditData(p => ({ ...p, betrag: parseFloat(e.target.value) || null }))} />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500">Turnus</label>
                    <select className="w-full mt-1 border rounded-lg p-2 text-sm" defaultValue={vorgang.turnus || 'monatlich'} onChange={e => setEditData(p => ({ ...p, turnus: e.target.value }))}>
                      <option value="monatlich">Monatlich</option>
                      <option value="quartalsweise">Quartalsweise</option>
                      <option value="jaehrlich">Jährlich</option>
                      <option value="einmalig">Einmalig</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs text-slate-500">Beginn</label><Input type="date" defaultValue={vorgang.beginn_datum || ''} onChange={e => setEditData(p => ({ ...p, beginn_datum: e.target.value }))} /></div>
                  <div><label className="text-xs text-slate-500">Ende</label><Input type="date" defaultValue={vorgang.ende_datum || ''} onChange={e => setEditData(p => ({ ...p, ende_datum: e.target.value }))} /></div>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Notizen</label>
                  <textarea className="w-full border rounded-lg p-2 text-sm mt-1 min-h-[80px]" defaultValue={vorgang.notizen || ''} onChange={e => setEditData(p => ({ ...p, notizen: e.target.value }))} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={saving} data-testid="vorgang-save-btn">
                    <Save className="w-4 h-4 mr-1" /> {saving ? 'Speichere...' : 'Speichern'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditMode(false)}>Abbrechen</Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="p-3 rounded-lg" style={{ backgroundColor: `${vorgang.farbe}08`, border: `1px solid ${vorgang.farbe}20` }}>
                  <p className="font-medium text-slate-800">{vorgang.bezeichnung || vorgang.typ_label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{vorgang.kategorie_label} &middot; {vorgang.typ_label}</p>
                  <Badge className="mt-1 text-xs" variant={vorgang.status === 'aktiv' ? 'default' : 'secondary'}>{vorgang.status}</Badge>
                </div>
                {vorgang.aktenzeichen && <div><p className="text-xs text-slate-400">Aktenzeichen</p><p className="font-medium">{vorgang.aktenzeichen}</p></div>}
                {vorgang.institution && <div><p className="text-xs text-slate-400">Institution</p><p className="font-medium">{vorgang.institution}</p></div>}
                {vorgang.sachbearbeiter && <div><p className="text-xs text-slate-400">Sachbearbeiter</p><p className="font-medium">{vorgang.sachbearbeiter}</p></div>}
                {vorgang.betrag > 0 && <div><p className="text-xs text-slate-400">Betrag</p><p className="font-medium">{Number(vorgang.betrag).toLocaleString('de-DE')} EUR / {vorgang.turnus || 'mtl.'}</p></div>}
                {vorgang.beginn_datum && <div><p className="text-xs text-slate-400">Beginn</p><p className="font-medium">{new Date(vorgang.beginn_datum).toLocaleDateString('de-DE')}</p></div>}
                {vorgang.notizen && <div><p className="text-xs text-slate-400">Notizen</p><p className="text-sm text-slate-600 whitespace-pre-wrap">{vorgang.notizen}</p></div>}
                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => { setEditMode(true); setEditData({}); }} data-testid="vorgang-edit-btn">
                    <Edit className="w-4 h-4 mr-1" /> Bearbeiten
                  </Button>
                  {vorgang.status === 'aktiv' && <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => handleStatusChange('beendet')}>Beenden</Button>}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Dokumente */}
          <TabsContent value="dokumente" className="mt-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <label className="cursor-pointer flex-1">
                  <input type="file" className="hidden" onChange={handleUpload} accept=".pdf,.doc,.docx,.jpg,.png,.txt" />
                  <Button size="sm" variant="outline" disabled={uploading} asChild className="w-full">
                    <span data-testid={`vorgang-upload-${vorgang.typ}`}>
                      <Upload className="w-4 h-4 mr-1" /> {uploading ? 'Lade...' : 'Hochladen'}
                    </span>
                  </Button>
                </label>
                <Button size="sm" variant="outline" onClick={loadAllDocs} data-testid="vorgang-zuordnen-btn">
                  <Link2 className="w-4 h-4 mr-1" /> Aus Akte zuordnen
                </Button>
              </div>

              {/* Zuordnen-Dialog */}
              {showAssign && (
                <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Dokument aus Akte zuordnen</p>
                    <Button size="sm" variant="ghost" onClick={() => setShowAssign(false)}><X className="w-4 h-4" /></Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {allDocs.filter(d => !dokumente.find(ed => ed.id === d.id)).map(d => (
                      <button
                        key={d.id}
                        onClick={() => handleAssign(d.id)}
                        className="w-full flex items-center gap-2 p-2 text-left rounded hover:bg-white text-sm"
                      >
                        <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-slate-700">{d.dateiname || d.titel || 'Dokument'}</p>
                          {d.beschreibung && <p className="text-xs text-slate-400 truncate">{d.beschreibung}</p>}
                        </div>
                        <Plus className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                      </button>
                    ))}
                    {allDocs.filter(d => !dokumente.find(ed => ed.id === d.id)).length === 0 && (
                      <p className="text-xs text-slate-400 text-center py-2">Keine weiteren Dokumente verfügbar</p>
                    )}
                  </div>
                </div>
              )}

              {loadingDocs ? (
                <div className="text-center py-8 text-slate-400">Lade Dokumente...</div>
              ) : dokumente.length > 0 ? (
                <div className="space-y-1.5">
                  {dokumente.map((doc, i) => (
                    <div key={doc.id || i} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`vorgang-dokument-${i}`}>
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-700 truncate">{doc.dateiname || doc.titel || 'Dokument'}</p>
                        {doc.beschreibung && <p className="text-xs text-slate-500 line-clamp-2">{doc.beschreibung}</p>}
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-slate-400">{doc.dokumententyp || 'Dokument'}</span>
                          {doc.created_date && <span className="text-xs text-slate-300">{new Date(doc.created_date).toLocaleDateString('de-DE')}</span>}
                          {doc.dateigroesse && <span className="text-xs text-slate-300">{(doc.dateigroesse / 1024).toFixed(0)} KB</span>}
                          {doc.zuordnung === 'direkt' && <Badge variant="outline" className="text-[9px] px-1 py-0">direkt</Badge>}
                        </div>
                      </div>
                      {doc.analyse_status === 'fertig' && <Check className="w-3.5 h-3.5 text-green-500 shrink-0" title="KI-analysiert" />}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FolderOpen className="w-10 h-10 text-slate-200 mx-auto mb-2" />
                  <p className="text-sm text-slate-400">Keine Dokumente in diesem Vorgang</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

// ===== Vorgang erstellen =====
function VorgangErstellen({ klientId, onClose, onCreated }) {
  const [katalog, setKatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKat, setSelectedKat] = useState('');
  const [selectedTyp, setSelectedTyp] = useState('');
  const [bezeichnung, setBezeichnung] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/vorgaenge/katalog`);
        if (res.ok) setKatalog(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, []);

  const selectedKatObj = katalog.find(k => k.kategorie === selectedKat);
  const typen = selectedKatObj?.typen || [];

  const handleCreate = async () => {
    if (!selectedTyp) return;
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/vorgaenge/klient/${klientId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ typ: selectedTyp, bezeichnung: bezeichnung || undefined }),
      });
      if (res.ok) onCreated();
      else {
        const err = await res.json();
        alert(err.detail || 'Fehler');
      }
    } catch (e) { console.error(e); }
    setCreating(false);
  };

  return (
    <Sheet open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="vorgang-erstellen-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Vorgang erstellen</SheetTitle>
        </SheetHeader>
        <div className="mt-6 space-y-5">
          {loading ? <p className="text-slate-400 text-sm text-center py-4">Lade Katalog...</p> : (
            <>
              {/* Kategorie wählen */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">1. Kategorie</label>
                <Select value={selectedKat} onValueChange={v => { setSelectedKat(v); setSelectedTyp(''); }}>
                  <SelectTrigger data-testid="vorgang-kategorie-select">
                    <SelectValue placeholder="Kategorie wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {katalog.map(k => {
                      const KIcon = getIcon(k.icon);
                      return (
                        <SelectItem key={k.kategorie} value={k.kategorie}>
                          <span className="flex items-center gap-2">
                            <KIcon className="w-4 h-4" style={{ color: k.farbe }} />
                            {k.kategorie_label}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              {/* Typ wählen */}
              {selectedKat && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">2. Typ</label>
                  <Select value={selectedTyp} onValueChange={setSelectedTyp}>
                    <SelectTrigger data-testid="vorgang-typ-select">
                      <SelectValue placeholder="Typ wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {typen.map(t => (
                        <SelectItem key={t.slug} value={t.slug}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Optionale Bezeichnung */}
              {selectedTyp && (
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">3. Bezeichnung (optional)</label>
                  <Input
                    placeholder="z.B. AOK Bayern, Amtsgericht Augsburg..."
                    value={bezeichnung}
                    onChange={e => setBezeichnung(e.target.value)}
                    data-testid="vorgang-bezeichnung-input"
                  />
                </div>
              )}

              {/* Erstellen Button */}
              <Button
                className="w-full" disabled={!selectedTyp || creating}
                onClick={handleCreate}
                data-testid="vorgang-erstellen-submit"
              >
                {creating ? 'Wird erstellt...' : 'Vorgang erstellen'}
              </Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default VorgaengeKacheln;
